import { useMemo } from "react";
import { getAccountNameWithLast4 } from "@common/helpers/financialAccount";
import type { FinancialAccountBase } from "@common/types/financialAccount";
import {
    AccountPickerIcon,
    getAccountPickerDescription,
} from "@components/financialAccount/AccountPickerItem";
import { accountMatchesSearch } from "@components/financialAccount/AccountsFilter/FinancialAccountCombobox.lib";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import {
    MAX_ENTITY_REF_OPTIONS,
    NO_ENTITY_REF_OPTIONS,
    type EntityRefOption,
    type EntityRefOptionsParams,
    type EntityRefOptionsResult,
} from "./entityRefOptions.types";

function toAccountOption(account: FinancialAccountBase): EntityRefOption {
    const title = getAccountNameWithLast4(account);
    return {
        key: `financialAccount:${account.id}`,
        entityRef: { kind: "financialAccount", id: account.id, label: title },
        icon: <AccountPickerIcon account={account} />,
        title,
        description: getAccountPickerDescription(account),
    };
}

export function useFinancialAccountRefOptions({
    query,
    isEnabled,
}: EntityRefOptionsParams): EntityRefOptionsResult {
    const accounts = useFinancialAccounts();
    const isLoading = useEntitiesContexts() === undefined;

    return useMemo(() => {
        if (!isEnabled) {
            return NO_ENTITY_REF_OPTIONS;
        }
        const options = accounts
            .filter((account) => !account.disabled)
            .filter((account) => accountMatchesSearch(account, query))
            .slice(0, MAX_ENTITY_REF_OPTIONS)
            .map(toAccountOption);
        return { options, isLoading };
    }, [isEnabled, accounts, query, isLoading]);
}
